import { Component } from '@theme/component';

class HoxoPartsCatalogue extends Component {
  cache = new Map();
  page = 1;
  revision = 0;

  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.productPage = this.dataset.productPage === 'true';
    this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
    this.form = this.querySelector('[data-filters]');
    this.addEventListener('change', event => this.change(event), { signal });
    this.form.addEventListener('submit', event => event.preventDefault(), { signal });
    this.form.addEventListener('reset', event => {
      event.preventDefault();
      this.navigate({ brand: this.state.brand });
    }, { signal });
    this.form.elements.q.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.filtersChanged(), 220);
    }, { signal });
    this.querySelector('[data-more]').addEventListener('click', () => { this.page++; this.renderGrid(); }, { signal });
    this.addEventListener('error', event => {
      if (event.target instanceof HTMLImageElement) { event.target.hidden = true; event.target.previousElementSibling.hidden = false; }
    }, { capture: true, signal });
    this.addEventListener('load', event => {
      if (event.target instanceof HTMLImageElement && event.target.closest('.hpc__picture')) event.target.previousElementSibling.hidden = true;
    }, { capture: true, signal });
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-image-zoom]')) this.querySelector('dialog')?.showModal();
      if (event.target instanceof HTMLDialogElement) event.target.close();
    }, { signal });
    window.addEventListener('hashchange', () => { if (!this.productPage) this.load(); }, { signal });
    window.addEventListener('popstate', () => { if (this.productPage && location.search !== this.pageSearch) this.load(); }, { signal });
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.timer);
    this.revision++;
    super.disconnectedCallback();
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }

  text(value) {
    const direct = { 'Met frame': 'with_frame', 'Zonder frame': 'without_frame', 'Niet gespecificeerd': 'unspecified', 'Service Pack (gemonteerd)': 'service_pack_assembled', 'Refurbished': 'refurbished' };
    if (direct[value]) return this.labels[direct[value]] || value;
    const words = { 'Roze Goud': 'colour_rose_gold', 'Roségoud': 'colour_rose_gold', 'Middernachtblauw': 'colour_midnight_blue', 'Spacegrijs': 'colour_space_grey', 'Standaard': 'standard', 'Zonder BMS/IC': 'without_bms', 'Met frame': 'with_frame', 'Zwart': 'colour_black', 'Wit': 'colour_white', 'Goud': 'colour_gold', 'Zilver': 'colour_silver', 'Grijs': 'colour_grey', 'Blauw': 'colour_blue', 'Groen': 'colour_green', 'Roze': 'colour_pink', 'Paars': 'colour_purple', 'Geel': 'colour_yellow', 'Oranje': 'colour_orange', 'Rood': 'colour_red', 'Brons': 'colour_bronze', 'Crème': 'colour_cream' };
    return String(value ?? '').replace(/\b(Roze Goud|Roségoud|Middernachtblauw|Spacegrijs|Standaard|Zonder BMS\/IC|Met frame|Zwart|Wit|Goud|Zilver|Grijs|Blauw|Groen|Roze|Paars|Geel|Oranje|Rood|Brons|Crème)\b/g, word => this.labels[words[word]] || word);
  }

  countLabel(count, key) {
    return `${count} ${this.labels[count === 1 ? `${key}_one` : key]}`;
  }

  partTitle(product) {
    const kind = this.labels[`kind_${product.kind}`];
    return kind ? `${kind} ${this.labels.for_word} ${product.model}` : product.title;
  }

  description(product) {
    const label = key => this.escape(this.labels[key]);
    const help = ['battery', 'assembly', 'panel'].includes(product.kind) ? product.kind : 'screen';
    const qualities = [...new Set(product.variants.map(v => this.text(v.quality)))].join(', ');
    return `<p>${this.escape(this.partTitle(product))}. ${label('selection_help')}</p><p>${label(`${help}_help`)}</p><h3>${label('specifications')}</h3><ul><li>${label('suitable')}${label('colon')} ${this.escape(product.model)}</li><li>${label('type')}${label('colon')} ${label(`kind_${product.kind}`)}</li><li>${label('qualities')}${label('colon')} ${this.escape(qualities)}</li></ul>${product.brand === 'samsung' && product.kind !== 'battery' ? `<p>${label('frame_help')}</p>` : ''}${product.brand === 'iphone' && product.kind === 'screen' ? `<p>${label('image_help')}</p>` : ''}<p>${label('fitting_help')}</p>`;
  }

  url(state) {
    return '#' + new URLSearchParams(Object.entries(state).filter(([, value]) => value)).toString();
  }

  pageUrl(state, detail = false) {
    const url = new URL(this.dataset.collectionUrl, location.origin);
    url.searchParams.set('view', detail ? 'parts-product' : 'parts-review');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    if (detail) {
      for (const [key, value] of Object.entries(state)) if (value) url.searchParams.set(key, value);
    } else url.hash = this.url(state);
    return url.pathname + url.search + url.hash;
  }

  navigate(state, replace = false) {
    const url = this.url(state);
    if (replace) { history.replaceState(null, '', url); this.load(); }
    else if (location.hash !== url) location.hash = url;
  }

  async load() {
    const revision = ++this.revision;
    this.pageSearch = location.search;
    const params = new URLSearchParams(this.productPage ? location.search : location.hash.slice(1));
    this.state = Object.fromEntries(['brand', 'model', 'kind', 'quality', 'frame', 'q', 'product', 'sku'].map(key => [key, params.get(key) || '']));
    if (!this.productPage && this.state.product) {
      location.replace(this.pageUrl(this.state, true));
      return;
    }
    if (!['iphone', 'samsung', 'macbook'].includes(this.state.brand)) this.state.brand = 'iphone';
    this.page = 1;
    for (const link of this.querySelectorAll('[data-brand]')) {
      if (link.dataset.brand === this.state.brand) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
    const browse = this.querySelector('[data-browse]');
    const detail = this.querySelector('[data-detail]');
    browse.hidden = this.productPage;
    detail.hidden = true;
    this.querySelector('[data-status]').textContent = this.labels.loading;
    try {
      if (!this.cache.has(this.state.brand)) {
        const response = await fetch(this.dataset[this.state.brand], { signal: this.controller.signal });
        if (!response.ok) throw new Error('Catalogue unavailable');
        const data = await response.json();
        if (revision !== this.revision) return;
        this.cache.set(this.state.brand, data);
      }
      if (revision !== this.revision) return;
      this.products = this.cache.get(this.state.brand);
      this.populateFilters();
      const product = this.products.find(p => p.handle === this.state.product);
      if (product) {
        browse.hidden = true;
        detail.hidden = false;
        this.querySelector('[data-page-status]').hidden = true;
        const variant = product.variants.find(v => v.sku === this.state.sku) || product.variants.find(v => this.matchesVariant(v)) || product.variants[0];
        this.renderDetail(product, variant);
      } else if (this.productPage) {
        this.querySelector('[data-page-status]').hidden = false;
        this.querySelector('[data-page-status] p').textContent = this.labels.empty;
      } else this.renderGrid();
    } catch (error) {
      if (error.name !== 'AbortError' && revision === this.revision) {
        this.querySelector('[data-status]').textContent = this.labels.error;
        this.querySelector('[data-page-status]').hidden = !this.productPage;
        this.querySelector('[data-page-status] p').textContent = this.labels.error;
        this.querySelector('[data-grid]').replaceChildren();
      }
    }
  }

  populateFilters() {
    const variants = this.products.flatMap(p => p.variants);
    const fields = { model: this.products.map(p => p.model), quality: variants.map(v => v.quality), frame: variants.map(v => v.frame) };
    for (const [name, values] of Object.entries(fields)) {
      const select = this.form.elements[name];
      select.replaceChildren(new Option(this.labels.all, ''));
      for (const value of [...new Set(values)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))) select.add(new Option(this.text(value), value));
      select.value = this.state[name] || '';
    }
    this.form.elements.q.value = this.state.q || '';
    this.form.elements.kind.value = this.state.kind || '';
    this.querySelector('[data-frame-filter]').hidden = this.state.brand !== 'samsung' || this.state.kind === 'battery';
  }

  filtersChanged() {
    clearTimeout(this.timer);
    const values = Object.fromEntries(new FormData(this.form));
    if (this.state.brand !== 'samsung' || values.kind === 'battery') values.frame = '';
    this.navigate({ brand: this.state.brand, ...values }, true);
  }

  matchesVariant(variant) {
    return (!this.state.quality || variant.quality === this.state.quality) && (!this.state.frame || variant.frame === this.state.frame);
  }

  imageFor(product, variant) {
    if (product.kind === 'battery') return variant?.image || product.image;
    return variant ? variant.image || (product.brand === 'iphone' && product.kind === 'screen' ? product.image : '') : product.image;
  }

  picture(url, alt, eager = false) {
    const safe = /^https:\/\//.test(url || '') ? url : '';
    return `<div class="hpc__picture"><span>${this.escape(this.labels.photo)}</span>${safe ? `<img src="${this.escape(safe)}" alt="${this.escape(alt)}" width="640" height="640" loading="${eager ? 'eager' : 'lazy'}" decoding="async"${eager ? ' fetchpriority="high"' : ''}>` : ''}</div>`;
  }

  renderGrid() {
    const search = (this.state.q || '').trim().toLocaleLowerCase();
    const results = this.products.filter(p => (!this.state.model || p.model === this.state.model) && (!this.state.kind || (p.kind === 'battery' ? 'battery' : 'screen') === this.state.kind) && p.variants.some(v => this.matchesVariant(v) && (!search || `${p.title} ${this.partTitle(p)} ${v.sku} ${v.detail} ${this.text(v.detail)}`.toLocaleLowerCase().includes(search))));
    this.querySelector('[data-status]').textContent = results.length ? this.countLabel(results.length, 'results') : this.labels.empty;
    this.querySelector('[data-grid]').innerHTML = results.slice(0, this.page * 24).map((p, index) => {
      const variants = p.variants.filter(v => this.matchesVariant(v));
      const qualities = [...new Set(variants.map(v => v.quality))];
      const url = this.pageUrl({ ...this.state, product: p.handle, sku: variants[0]?.sku }, true);
      return `<article class="hpc__card"><a class="hpc__image-link" href="${this.escape(url)}" aria-label="${this.escape(this.partTitle(p))}">${this.picture(this.imageFor(p), this.partTitle(p) + (p.kind === 'battery' ? ` · ${this.labels.illustrative}` : ''), index < 4)}</a><p class="hpc__meta">${this.escape(p.brand === 'macbook' ? 'Apple MacBook' : p.brand === 'iphone' ? 'Apple iPhone' : 'Samsung')} · ${this.escape(this.countLabel(variants.length, 'variants'))}</p><h2><a href="${this.escape(url)}">${this.escape(this.partTitle(p))}</a></h2><div class="hpc__quality-list">${qualities.map(q => `<span>${this.escape(this.text(q))}</span>`).join('')}</div><a class="hpc__choose" href="${this.escape(url)}">${this.escape(this.labels.choose)}<span aria-hidden="true">→</span></a></article>`;
    }).join('');
    this.querySelector('[data-more]').hidden = results.length <= this.page * 24;
  }

  choice(name, label, values, selected) {
    return `<fieldset><legend>${this.escape(label)}</legend><div class="hpc__pills">${[...new Set(values)].map(value => `<label class="hpc__pill"><input type="radio" name="${name}" value="${this.escape(value)}" ${value === selected ? 'checked' : ''}><span>${this.escape(this.text(value))}</span></label>`).join('')}</div></fieldset>`;
  }

  renderDetail(product, variant) {
    this.product = product;
    this.variant = variant;
    const state = { ...this.state };
    delete state.product;
    delete state.sku;
    const qualities = product.variants.map(v => v.quality);
    const matchingQuality = product.variants.filter(v => v.quality === variant.quality);
    const frame = product.brand === 'samsung' && product.kind !== 'battery';
    const configurations = matchingQuality.filter(v => !frame || v.frame === variant.frame);
    let choices = this.choice('quality', this.labels.quality, qualities, variant.quality);
    if (frame) choices += this.choice('frame', this.labels.frame, matchingQuality.map(v => v.frame), variant.frame);
    if (configurations.length > 1 || variant.detail !== 'Standaard') choices += this.choice('detail', this.labels.configuration, configurations.map(v => v.detail), variant.detail);
    const isIllustration = product.kind === 'battery' || (product.brand === 'iphone' && product.kind === 'screen');
    const title = this.partTitle(product);
    const label = key => this.escape(this.labels[key]);
    const image = this.imageFor(product, variant);
    const hasImage = /^https:\/\//.test(image || '');
    const help = ['battery', 'assembly', 'panel'].includes(product.kind) ? product.kind : 'screen';
    const brand = product.brand === 'iphone' ? 'Apple iPhone' : product.brand === 'macbook' ? 'Apple MacBook' : 'Samsung';
    const facts = [[this.labels.suitable, product.model], [this.labels.type, this.labels[`kind_${product.kind}`]], [this.labels.quality, this.text(variant.quality)]];
    if (frame) facts.push([this.labels.frame, this.text(variant.frame)]);
    if (variant.detail !== 'Standaard') facts.push([this.labels.configuration, this.text(variant.detail)]);
    facts.push([this.labels.sku, variant.sku]);
    const factsHtml = facts.map(([key, value]) => `<div><dt>${this.escape(key)}</dt><dd>${this.escape(value)}</dd></div>`).join('');
    const selected = [this.text(variant.quality), ...(frame ? [this.text(variant.frame)] : []), ...(variant.detail !== 'Standaard' ? [this.text(variant.detail)] : [])].join(' · ');
    document.title = `${title} – HOXO`;
    for (const form of document.querySelectorAll('form[action$="/localization"]')) {
      const returnTo = form.querySelector('input[name="return_to"]');
      if (returnTo) returnTo.value = location.pathname + location.search;
    }
    this.querySelector('[data-detail]').innerHTML = `
      <nav class="hpc__breadcrumbs" aria-label="${label('breadcrumb')}">
        <a href="${this.escape(this.pageUrl(state))}">${label('title')}</a><span aria-hidden="true">/</span>
        <a href="${this.escape(this.pageUrl({ brand: product.brand }))}">${this.escape(brand)}</a><span aria-hidden="true">/</span>
        <span aria-current="page">${this.escape(product.model)}</span>
      </nav>
      <a class="hpc__back" href="${this.escape(this.pageUrl(state))}"><span aria-hidden="true">←</span> ${label('back')}</a>
      <div class="hpc__product">
        <div class="hpc__gallery">
          ${hasImage ? `<button class="hpc__zoom" type="button" data-image-zoom aria-label="${label('zoom_image')}">${this.picture(image, title, true)}<span class="hpc__zoom-hint" aria-hidden="true">⊕</span></button>` : this.picture('', title, true)}
          ${isIllustration ? `<p class="hpc__caption">${label(product.kind === 'battery' ? 'illustrative' : 'image_help')}</p>` : ''}
        </div>
        <div class="hpc__product-summary">
          <p class="hpc__meta">${this.escape(brand)} · ${this.escape(this.countLabel(product.variants.length, 'variants'))}</p>
          <h1>${this.escape(title)}</h1>
          <p class="hpc__intro">${label(`${help}_help`)}</p>
          <form class="hpc__choices" data-choices>${choices}</form>
          <div class="hpc__selection" role="status"><strong>${label('selected')}</strong><p>${this.escape(selected)}</p><p>${label('sku')}${label('colon')} <strong>${this.escape(variant.sku)}</strong></p>${frame && variant.frame === 'Niet gespecificeerd' ? `<p class="hpc__frame-note">${label('frame_note')}</p>` : ''}</div>
          <p class="hpc__price-note">${label('no_prices')}</p>
          <div class="hpc__quick-facts"><h2>${label('glance')}</h2><dl>${factsHtml}</dl></div>
          <a class="hpc__details-link" href="#hpc-product-information">${label('all_details')} <span aria-hidden="true">↓</span></a>
        </div>
      </div>
      <div id="hpc-product-information" class="hpc__information">
        <div><h2>${label('details')}</h2><div class="hpc__description">${this.description(product)}</div></div>
        <aside class="hpc__help"><h2>${label('help_title')}</h2><p>${label('help_text')}</p><a href="mailto:info@hoxo-shop.com?subject=${encodeURIComponent(title + ' · ' + variant.sku)}">info@hoxo-shop.com</a></aside>
      </div>
      ${hasImage ? `<dialog class="hpc__image-dialog" aria-label="${this.escape(title)}"><form method="dialog"><button autofocus aria-label="${label('close_image')}">×</button></form>${this.picture(image, title, true)}</dialog>` : ''}`;
  }

  change(event) {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    if (event.target.closest('[data-filters]')) { this.filtersChanged(); return; }
    if (!event.target.closest('[data-choices]')) return;
    const { name, value } = event.target;
    const previous = this.variant;
    const candidates = this.product.variants.filter(v => v[name] === value && (name === 'quality' || v.quality === previous.quality) && (name !== 'detail' || v.frame === previous.frame));
    const variant = candidates.find(v => v.frame === previous.frame && v.detail === previous.detail) || candidates.find(v => v.frame === previous.frame) || candidates[0];
    if (!variant) return;
    this.state.sku = variant.sku;
    history.replaceState(null, '', this.pageUrl(this.state, true));
    this.pageSearch = location.search;
    this.renderDetail(this.product, variant);
    const selected = [...this.querySelectorAll('[data-choices] input')].find(input => input.name === name && input.value === value);
    selected?.focus({ preventScroll: true });
  }
}

if (!customElements.get('hoxo-parts-catalogue')) customElements.define('hoxo-parts-catalogue', HoxoPartsCatalogue);



